import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "next-themes";
import { Toaster, TooltipProvider } from "@/shared/components/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { KeycloakAuthProvider, AzureAuthProvider, SentryUserSync } from "@/features/auth";
import { ProcessingProvider, UploadProvider, StreamingProvider } from "@/shared/contexts";
import { ProtectedRoute } from "@/shared/components/ProtectedRoute";
import { AdminRoute } from "@/shared/components/AdminRoute";
import { AuthCallback } from "@/shared/components/AuthCallback";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { PageLoader, CenteredLoader } from "@/shared/components/PageLoader";
import { AppLayout } from "@/shared/components/AppLayout";
import { UploadIndicator } from "@/shared/components/UploadIndicator";
import { StreamingIndicator } from "@/shared/components/StreamingIndicator";


// Carregamento lazy de páginas para melhor desempenho no carregamento inicial
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NewProject = lazy(() => import("./pages/NewProject"));
const ProjectView = lazy(() => import("./pages/ProjectView"));
const SessionView = lazy(() => import("./pages/SessionView"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dados sao considerados "frescos" por 2 minutos
      // Durante esse tempo, NAO faz nova chamada ao backend
      staleTime: 2 * 60 * 1000,

      // Mantem dados no cache por 10 minutos mesmo apos nao serem usados
      // Permite navegar entre paginas sem refetch
      gcTime: 10 * 60 * 1000,

      // NAO refaz chamada quando usuario volta para a aba
      refetchOnWindowFocus: false,

      // NAO refaz chamada quando reconecta a internet
      refetchOnReconnect: false,

      // Retry apenas 1 vez em caso de erro
      retry: 1,

      // Intervalo entre retries
      retryDelay: 1000,
    },
  },
});

/**
 * Fábrica em Tempo de Execução: Determina qual provedor de autenticação usar com base em variáveis de ambiente
 * Esta decisão acontece exatamente uma vez, na raiz da aplicação
 */
const getAuthProviderType = (): 'azure' | 'keycloak' => {
  // Verificar se Azure está configurado
  const azureClientId = import.meta.env.VITE_AZURE_CLIENT_ID;
  const azureTenantId = import.meta.env.VITE_AZURE_TENANT_ID;
  const azureAuthority = import.meta.env.VITE_AZURE_AUTHORITY;

  // Verificar se Keycloak está configurado
  const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL;
  const keycloakRealm = import.meta.env.VITE_KEYCLOAK_REALM;
  const keycloakClientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;

  // Prioridade: Azure > Keycloak
  if (azureClientId || azureTenantId || azureAuthority) {
    return 'azure';
  }

  // Padrão para Keycloak se configurado ou como fallback
  if (keycloakUrl && keycloakRealm && keycloakClientId) {
    return 'keycloak';
  }

  // Fallback para Keycloak
  return 'keycloak';
};

// Selecionar o provedor de autenticação em tempo de execução
const CurrentAuthProvider = getAuthProviderType() === 'azure'
  ? AzureAuthProvider
  : KeycloakAuthProvider;

/**
 * ProtectedAppRoute - Combines ProtectedRoute with AppLayout and ProjectsProvider
 * Used for authenticated routes that should display with sidebar
 * 
 * O Suspense fica DENTRO do AppLayout para que o sidebar permaneca
 * visivel durante transicoes entre paginas (evita flash de tela branca)
 */
function ProtectedAppRoute({ children, section }: { children: React.ReactNode; section: string }) {
  const { t: tCommon } = useTranslation("common");
  return (
    <ProtectedRoute>
      <AppLayout>
        <ErrorBoundary section={section}>
          <Suspense fallback={<CenteredLoader message={tCommon("loading")} />}>
            {children}
          </Suspense>
        </ErrorBoundary>
      </AppLayout>
    </ProtectedRoute>
  );
}

const App = () => {
  const { t: tCommon } = useTranslation("common");
  const { t: tProject } = useTranslation("project");
  const { t: tAdmin } = useTranslation("admin");
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <ErrorBoundary section={tCommon("application")}>
      <QueryClientProvider client={queryClient}>
        <CurrentAuthProvider>
          <SentryUserSync />
          <ProcessingProvider>
            <UploadProvider>
              <TooltipProvider>
                <Toaster />
                <BrowserRouter>
                  <StreamingProvider>
                    <Routes>
                      {/* Public Routes - No sidebar, com Suspense proprio */}
                      <Route path="/" element={
                        <Suspense fallback={<PageLoader />}>
                          <Index />
                        </Suspense>
                      } />
                      <Route path="/login" element={
                        <Suspense fallback={<PageLoader />}>
                          <Login />
                        </Suspense>
                      } />
                      <Route path="/register" element={
                        <Suspense fallback={<PageLoader />}>
                          <Register />
                        </Suspense>
                      } />
                      <Route path="/access-denied" element={
                        <Suspense fallback={<PageLoader />}>
                          <AccessDenied />
                        </Suspense>
                      } />
                      <Route path="/callback" element={<AuthCallback />} />

                      {/* Protected Routes - With sidebar layout */}
                      {/* O Suspense esta DENTRO do ProtectedAppRoute para manter sidebar visivel */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedAppRoute section="Dashboard">
                            <Dashboard />
                          </ProtectedAppRoute>
                        }
                      />
                      <Route
                        path="/dashboard/new-project"
                        element={
                          <ProtectedAppRoute section={tProject("newProject")}>
                            <NewProject />
                          </ProtectedAppRoute>
                        }
                      />
                      <Route
                        path="/dashboard/project/:projectId"
                        element={
                          <ProtectedAppRoute section={tCommon("project")}>
                            <ProjectView />
                          </ProtectedAppRoute>
                        }
                      />
                      <Route
                        path="/dashboard/project/:projectId/session/:sessionId"
                        element={
                          <ProtectedAppRoute section={tCommon("session")}>
                            <SessionView />
                          </ProtectedAppRoute>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <ProtectedAppRoute section={tAdmin("title")}>
                            <AdminRoute>
                              <AdminPage />
                            </AdminRoute>
                          </ProtectedAppRoute>
                        }
                      />

                      {/* 404 - No sidebar */}
                      <Route path="*" element={
                        <Suspense fallback={<PageLoader />}>
                          <NotFound />
                        </Suspense>
                      } />
                    </Routes>
                    {/* Global Upload Indicator - shows upload progress anywhere in the app */}
                    <UploadIndicator />
                    {/* Indicador Global de Streaming - mostra processamento do agente em qualquer lugar do app */}
                    <StreamingIndicator />
                  </StreamingProvider>
                </BrowserRouter>
              </TooltipProvider>
            </UploadProvider>
          </ProcessingProvider>
        </CurrentAuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
